import { useMemo } from "react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";
import {
  calcularTasaConversion,
  predecirVentas,
  analisisPorHora,
  analizarPatronesCompra,
  proyectarIngresos,
} from "../../../utils/BoleteriaUtils";

const PredictiveStats = ({ reportes, evento }) => {
  // Calcular todas las métricas predictivas
  const tasasConversion = useMemo(
    () => calcularTasaConversion(reportes, evento),
    [reportes, evento]
  );

  const prediccion = useMemo(
    () => predecirVentas(reportes, evento),
    [reportes, evento]
  );

  const ventasPorHora = useMemo(
    () => analisisPorHora(reportes),
    [reportes]
  );

  const patronesCompra = useMemo(
    () => analizarPatronesCompra(reportes),
    [reportes]
  );

  const proyeccion = useMemo(
    () => proyectarIngresos(reportes, prediccion),
    [reportes, prediccion]
  );

  if (!tasasConversion || !prediccion || !reportes || !evento) {
    return (
      <div className="container-fluid my-4">
        <div className="card shadow-sm">
          <div className="card-body text-center py-5">
            <i className="bi bi-graph-up text-muted" style={{ fontSize: "3rem" }}></i>
            <p className="text-muted mt-3">No hay datos suficientes para generar predicciones</p>
          </div>
        </div>
      </div>
    );
  }

  // Formatear moneda
  const formatCurrency = (value) => {
    return `$${parseFloat(value).toLocaleString("es-AR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Formatear fecha
  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("es-AR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  // Obtener color según estado
  const getEstadoColor = (estado) => {
    switch (estado) {
      case "sold_out":
        return "danger";
      case "alta_demanda":
        return "success";
      case "baja_demanda":
        return "warning";
      default:
        return "info";
    }
  };

  const getEstadoTexto = (estado) => {
    switch (estado) {
      case "sold_out":
        return "Agotamiento Próximo";
      case "alta_demanda":
        return "Alta Demanda";
      case "baja_demanda":
        return "Baja Demanda";
      default:
        return "Demanda Normal";
    }
  };

  // Datos para gráfico de proyección
  const datosProyeccion = [
    {
      categoria: "Vendidas",
      cantidad: tasasConversion.entradasVendidas,
      tipo: "actual",
    },
    {
      categoria: "Proyectadas",
      cantidad: prediccion.ventasProyectadas,
      tipo: "proyeccion",
    },
    {
      categoria: "Disponibles",
      cantidad: tasasConversion.stockDisponible,
      tipo: "disponible",
    },
  ];

  return (
    <div className="container-fluid my-4">
      {/* Alerta de estado */}
      <div className={`alert alert-${getEstadoColor(prediccion.estadoVentas)} mb-4`} role="alert">
        <div className="d-flex align-items-center">
          <i className="bi bi-exclamation-triangle-fill me-3" style={{ fontSize: "1.5rem" }}></i>
          <div>
            <h6 className="alert-heading mb-1">
              <strong>{getEstadoTexto(prediccion.estadoVentas)}</strong>
            </h6>
            <p className="mb-0 small">{prediccion.recomendacion}</p>
          </div>
        </div>
      </div>

      {/* Tarjetas de métricas principales */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted small mb-1">Entradas Vendidas</p>
                  <h3 className="mb-0 fw-bold text-primary">
                    {tasasConversion.entradasVendidas}
                  </h3>
                  <small className="text-muted">
                    de {tasasConversion.stockTotal} disponibles
                  </small>
                </div>
                <i className="bi bi-ticket-perforated text-primary" style={{ fontSize: "2rem" }}></i>
              </div>
              <div className="progress mt-2" style={{ height: "6px" }}>
                <div
                  className="progress-bar bg-primary"
                  role="progressbar"
                  style={{ width: `${tasasConversion.porcentajeVendido}%` }}
                ></div>
              </div>
              <small className="text-muted">{tasasConversion.porcentajeVendido}% vendido</small>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted small mb-1">Proyección Total</p>
                  <h3 className="mb-0 fw-bold text-success">
                    {prediccion.ventasTotalesProyectadas}
                  </h3>
                  <small className="text-muted">
                    +{prediccion.ventasProyectadas} estimadas
                  </small>
                </div>
                <i className="bi bi-graph-up-arrow text-success" style={{ fontSize: "2rem" }}></i>
              </div>
              <div className="progress mt-2" style={{ height: "6px" }}>
                <div
                  className="progress-bar bg-success"
                  role="progressbar"
                  style={{ width: `${prediccion.porcentajeProyectado}%` }}
                ></div>
              </div>
              <small className="text-muted">{prediccion.porcentajeProyectado}% proyectado</small>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted small mb-1">Velocidad de Venta</p>
                  <h3 className="mb-0 fw-bold text-info">
                    {tasasConversion.ventasDiarias}
                  </h3>
                  <small className="text-muted">entradas/día</small>
                </div>
                <i className="bi bi-speedometer2 text-info" style={{ fontSize: "2rem" }}></i>
              </div>
              <small className="text-muted d-block mt-2">
                <i className="bi bi-calendar-check me-1"></i>
                {tasasConversion.diasDesdeInicio} días de venta
              </small>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted small mb-1">
                    {prediccion.fechaSoldOut ? "Sold Out Estimado" : "Días al Evento"}
                  </p>
                  <h3 className="mb-0 fw-bold text-warning">
                    {prediccion.diasParaSoldOut || tasasConversion.diasHastaEvento}
                  </h3>
                  <small className="text-muted">días</small>
                </div>
                <i className="bi bi-hourglass-split text-warning" style={{ fontSize: "2rem" }}></i>
              </div>
              {prediccion.fechaSoldOut && (
                <small className="text-muted d-block mt-2">
                  <i className="bi bi-calendar-x me-1"></i>
                  {formatDate(prediccion.fechaSoldOut)}
                </small>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Proyección de ingresos */}
      {proyeccion && (
        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <h6 className="fw-bold mb-3">
              <i className="bi bi-currency-dollar me-2 text-success"></i>
              Proyección de Ingresos
            </h6>
            <div className="row">
              <div className="col-md-3">
                <div className="text-center p-3 bg-light rounded">
                  <p className="text-muted small mb-1">Ingresos Actuales</p>
                  <h5 className="fw-bold text-success mb-0">
                    {formatCurrency(proyeccion.ingresosActuales)}
                  </h5>
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-center p-3 bg-light rounded">
                  <p className="text-muted small mb-1">Precio Promedio</p>
                  <h5 className="fw-bold text-info mb-0">
                    {formatCurrency(proyeccion.precioPromedio)}
                  </h5>
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-center p-3 bg-light rounded">
                  <p className="text-muted small mb-1">Ingresos Proyectados</p>
                  <h5 className="fw-bold text-warning mb-0">
                    {formatCurrency(proyeccion.ingresosProyectados)}
                  </h5>
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-center p-3 bg-success bg-opacity-10 rounded border border-success">
                  <p className="text-muted small mb-1">Total Proyectado</p>
                  <h5 className="fw-bold text-success mb-0">
                    {formatCurrency(proyeccion.ingresosTotalesProyectados)}
                  </h5>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gráficos */}
      <div className="row">
        {/* Gráfico de proyección de ventas */}
        <div className="col-md-6 mb-4">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="fw-bold mb-3">
                <i className="bi bi-bar-chart me-2 text-primary"></i>
                Proyección de Ventas
              </h6>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={datosProyeccion}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="categoria" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="cantidad" fill="#d0552a" name="Entradas" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Gráfico de ventas por hora */}
        <div className="col-md-6 mb-4">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="fw-bold mb-3">
                <i className="bi bi-clock me-2 text-info"></i>
                Patrón de Ventas por Hora del Día
              </h6>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={ventasPorHora}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="hora"
                    tick={{ fontSize: 10 }}
                    interval={2}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="cantidad"
                    stroke="#0088FE"
                    fill="#0088FE"
                    fillOpacity={0.6}
                    name="Entradas"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Patrones de compra */}
        {patronesCompra && (
          <div className="col-md-12">
            <div className="card shadow-sm">
              <div className="card-body">
                <h6 className="fw-bold mb-3">
                  <i className="bi bi-basket me-2 text-warning"></i>
                  Análisis de Patrones de Compra
                </h6>
                <div className="row">
                  <div className="col-md-4">
                    <div className="text-center p-3 bg-light rounded">
                      <i className="bi bi-calculator text-primary" style={{ fontSize: "2rem" }}></i>
                      <p className="text-muted small mt-2 mb-1">Promedio por Compra</p>
                      <h4 className="fw-bold text-primary mb-0">
                        {patronesCompra.promedioEntradasPorCompra}
                      </h4>
                      <small className="text-muted">entradas</small>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-center p-3 bg-light rounded">
                      <i className="bi bi-star-fill text-warning" style={{ fontSize: "2rem" }}></i>
                      <p className="text-muted small mt-2 mb-1">Cantidad Más Frecuente</p>
                      <h4 className="fw-bold text-warning mb-0">
                        {patronesCompra.cantidadMasFrecuente}
                      </h4>
                      <small className="text-muted">entradas</small>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-center p-3 bg-light rounded">
                      <i className="bi bi-receipt text-info" style={{ fontSize: "2rem" }}></i>
                      <p className="text-muted small mt-2 mb-1">Total Transacciones</p>
                      <h4 className="fw-bold text-info mb-0">
                        {patronesCompra.totalTransacciones}
                      </h4>
                      <small className="text-muted">compras</small>
                    </div>
                  </div>
                </div>

                {/* Tabla de distribución */}
                <div className="mt-4">
                  <h6 className="text-muted mb-3">Distribución de Compras</h6>
                  <div className="table-responsive">
                    <table className="table table-sm table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Cantidad de Entradas</th>
                          <th className="text-center">Transacciones</th>
                          <th className="text-center">Porcentaje</th>
                          <th>Distribución</th>
                        </tr>
                      </thead>
                      <tbody>
                        {patronesCompra.distribucion.slice(0, 10).map((item, index) => (
                          <tr key={index}>
                            <td>
                              <strong>{item.cantidad}</strong> entrada{item.cantidad > 1 ? "s" : ""}
                            </td>
                            <td className="text-center">
                              <span className="badge bg-primary">{item.frecuencia}</span>
                            </td>
                            <td className="text-center">
                              <span className="badge bg-info">{item.porcentaje}%</span>
                            </td>
                            <td>
                              <div className="progress" style={{ height: "20px" }}>
                                <div
                                  className="progress-bar"
                                  role="progressbar"
                                  style={{ width: `${item.porcentaje}%` }}
                                >
                                  {item.porcentaje}%
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PredictiveStats;
